import React, { FC, useContext, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { IMenuItem } from '../Navigation/Navigation';
import Logo from '../../../static/icons/logo-nav.svg';
import { Link } from 'gatsby';
import { rem } from 'polished';
import { useLocation } from '@reach/router';
import LocaleContext from '../../context/Locale';
import { pxToRem } from '../../utils/pxToRem';

const overlayVariants = {
    initial: { x: '100%' },
    animate: {
        x: 0,
        transition: { type: 'tween' as const, duration: 0.35, ease: [0.4, 0, 0.2, 1] },
    },
    exit: {
        x: '100%',
        transition: { type: 'tween' as const, duration: 0.28, ease: [0.4, 0, 1, 1] },
    },
};

const listVariants = {
    animate: {
        transition: { staggerChildren: 0.07, delayChildren: 0.18 },
    },
    exit: {
        transition: { staggerChildren: 0.04, staggerDirection: -1 as const },
    },
};

const itemVariants = {
    initial: { opacity: 0, x: 32 },
    animate: {
        opacity: 1,
        x: 0,
        transition: { type: 'tween' as const, duration: 0.28, ease: [0.4, 0, 0.2, 1] },
    },
    exit: {
        opacity: 0,
        x: 32,
        transition: { type: 'tween' as const, duration: 0.18 },
    },
};

type Props = {
    elements: IMenuItem[];
    closeModal: () => void;
};

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const MenuMobileContent: FC<Props> = ({ closeModal, elements }) => {
    const { pathname } = useLocation();
    const { activeLocale } = useContext(LocaleContext);
    const overlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const overlay = overlayRef.current;

        const getFocusable = () =>
            overlay ? Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];

        getFocusable()[0]?.focus();

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeModal();
                return;
            }
            if (e.key !== 'Tab') return;

            const focusable = getFocusable();
            if (!focusable.length) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus();
        };
    }, []);

    return (
        <Overlay ref={overlayRef} role="dialog" aria-modal="true" aria-label="Menu nawigacyjne" variants={overlayVariants} initial="initial" animate="animate" exit="exit">
            <TopSection>
                <Link to="/" onClick={closeModal} aria-label="Strona główna MAGMED">
                    <LogoContainer>
                        <Logo width={180} aria-hidden="true" />
                    </LogoContainer>
                </Link>
            </TopSection>

            <NavList variants={listVariants}>
                {elements.map((item) => {
                    const isActive = pathname.includes(item.slug);
                    return (
                        <motion.li key={item.slug} variants={itemVariants}>
                            <NavLink
                                to={`/${activeLocale === 'en' ? 'en' : 'pl'}${item.slug}`}
                                onClick={closeModal}
                                $active={isActive}
                            >
                                {item.name}
                            </NavLink>
                        </motion.li>
                    );
                })}
            </NavList>
        </Overlay>
    );
};

const Overlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-primary);
    z-index: 10000;
    display: flex;
    flex-direction: column;
    padding: 0 ${rem(28)};
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -${rem(80)};
        right: -${rem(60)};
        width: ${rem(280)};
        height: ${rem(280)};
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.05);
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -${rem(60)};
        left: -${rem(50)};
        width: ${rem(220)};
        height: ${rem(220)};
        border-radius: 50%;
        background: rgba(0, 122, 114, 0.2);
        pointer-events: none;
    }
`;

const TopSection = styled.div`
    padding-top: calc(${pxToRem(48)} + ${rem(64)});
    padding-bottom: ${rem(36)};
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    margin-bottom: ${rem(12)};
`;

const LogoContainer = styled.div`
    filter: brightness(0) invert(1);
    display: inline-block;
`;

const NavList = styled(motion.ul)`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
`;

const NavLink = styled(Link)<{ $active: boolean }>`
    display: block;
    text-decoration: none;
    font-size: ${rem(26)};
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${(props) => (props.$active ? '#38D3CB' : 'rgba(255, 255, 255, 0.92)')};
    padding: ${rem(18)} 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    transition: color 200ms ease, transform 200ms ease;

    &:hover {
        color: #38D3CB;
        transform: translateX(${rem(6)});
    }

    &:focus-visible {
        outline: 2px solid #38D3CB;
        outline-offset: 4px;
        border-radius: 2px;
    }
`;
